import React from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../../../components/ui/Modal";
import { Calendar, Clock, Trash2, Edit, Compass, ChevronRight } from "lucide-react";

export interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  isAllDay: boolean;
  isSystem?: boolean;
  // Koç planı kalemleri metadata.source === "coach" ile gelir.
  metadata?: { source?: string; itemType?: string; studyPlanItemId?: string } | null;
}

interface Props {
  isOpen: boolean;
  event: CalendarEvent | null;
  canDelete: boolean;
  canUpdate: boolean;
  loading: boolean;
  onClose: () => void;
  onDelete: () => void;
  onEdit: () => void;
  formatDate: (date: string) => string;
  formatTime: (date: string) => string;
}

export default function ViewEventModal({
  isOpen,
  event,
  canDelete,
  canUpdate,
  loading,
  onClose,
  onDelete,
  onEdit,
  formatDate,
  formatTime,
}: Props) {
  const navigate = useNavigate();
  const isCoach = event?.metadata?.source === "coach";

  return (
    <Modal isOpen={isOpen && !!event} onClose={onClose} title={isCoach ? "Koç Planı" : "Etkinlik Detayları"} size="md">
      {event && (
        <>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-xl text-gray-900 mb-3">{event.title}</h4>
              <div className="flex flex-wrap gap-2 mb-3">
                {isCoach && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 border border-indigo-200">
                    <Compass className="w-3 h-3" />
                    Koçunun Planı
                  </span>
                )}
                {event.isAllDay ? (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                    <Calendar className="w-3 h-3" />
                    Tüm Gün
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                    <Clock className="w-3 h-3" />
                    Saatli Etkinlik
                  </span>
                )}
                {event.isSystem && !isCoach && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                    Sistem Etkinliği
                  </span>
                )}
              </div>
            </div>

            {/* Koç kalemi buradan yapılmaz — öğrenciyi işin yapılacağı yere götür */}
            {isCoach && (
              <button
                onClick={() => {
                  onClose();
                  navigate("/navigator");
                }}
                className="flex w-full items-center gap-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-left transition-colors hover:bg-indigo-100"
              >
                <Compass className="h-5 w-5 shrink-0 text-indigo-600" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900">Koçum'da yap</p>
                  <p className="text-xs text-gray-500">Bu çalışmayı başlatmak için Koçum sayfasına git</p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-indigo-400" />
              </button>
            )}

            {event.description && (
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-sm text-gray-700 leading-relaxed">{event.description}</p>
              </div>
            )}

            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 p-1.5 bg-green-100 rounded-lg">
                  <Calendar className="w-4 h-4 text-green-700" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 mb-0.5">Başlangıç</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatDate(event.startDate)}
                    {!event.isAllDay && <span className="ml-2 text-gray-600 font-normal">{formatTime(event.startDate)}</span>}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="mt-0.5 p-1.5 bg-red-100 rounded-lg">
                  <Calendar className="w-4 h-4 text-red-700" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 mb-0.5">Bitiş</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatDate(event.endDate)}
                    {!event.isAllDay && <span className="ml-2 text-gray-600 font-normal">{formatTime(event.endDate)}</span>}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-5 flex justify-between gap-2 border-t border-gray-200 mt-5">
            {canDelete && !isCoach ? (
              <button
                onClick={onDelete}
                disabled={loading}
                className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors">
                <Trash2 className="w-4 h-4" />
                Sil
              </button>
            ) : (
              <div />
            )}
            <div className="flex gap-2">
              {canUpdate && !isCoach && (
                <button
                  onClick={onEdit}
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                  <Edit className="w-4 h-4" />
                  Düzenle
                </button>
              )}
              <button
                onClick={onClose}
                className="bg-white px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                Kapat
              </button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
